import { deletePushTokenMutation } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';

import { getPushRegistration, loadFirebaseMessaging, VAPID_KEY } from './firebaseMessaging';
import { releasePushToken } from './pushToken';

/**
 * Revokes the Firebase token with OUR registration. Without it Firebase looks for its default
 * worker on its own terms, and the delete fails silently.
 *
 * Only when permission is already granted: `getToken` prompts on 'default', and sign-out must
 * never ask for permission.
 */
async function deleteFirebaseToken(): Promise<void> {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (window.Notification.permission !== 'granted') return;
  const registration = await getPushRegistration();
  if (!registration) return;
  const { messaging, getToken, deleteToken } = await loadFirebaseMessaging();
  if (!messaging) return;
  // Binds the registration to the messaging instance; returns the cached token, not a new one.
  await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  await deleteToken(messaging);
}

/**
 * The one place sign-out releases push. Every sign-out (sidebar account menu, mobile drawer) goes
 * through logoutUserProfileSaga, which runs this BEFORE POST /api/auth/logout, because deleting
 * the backend row needs the session that call destroys.
 */
export function signOutPush(): Promise<void> {
  let storage: Storage | null = null;
  try {
    storage = typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    storage = null;
  }
  return releasePushToken({
    storage,
    deleteRemote: (token) => fetchGraphQLApiData(deletePushTokenMutation(token)),
    deleteLocal: deleteFirebaseToken,
    log: (message, detail) => console.warn(message, detail ?? ''),
  });
}
